import * as Actions from '../actions/contactActions';
import Contact from '../../entities/contact';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type ContactAction = { 
    type: string; 
    contact: Contact | null; 
    contactId: string | Contact; 
    partial: boolean; 
    lastFetch: number; 
    contacts: Contact[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<Contact> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {Contact[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<Contact> | null, firmId: string, userId: string, updates?: Contact[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'contact', 'contacts', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<Contact>} data
 * @param {Contact[]} [updates]
 * @returns {ArrayMap<Contact>}
 */
function updateStorage(data: ArrayMap<Contact>, updates?: Contact[]): ArrayMap<Contact> {
	if (logging) console.debug('⮂ contactReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {Contact[]} [updates]
 * @returns {ArrayMap<Contact>}
 */
function updateStorageNoReturn(updates?: Contact[]): void {
	if (logging) console.debug('⮂ contactReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {Contact[]} [data]
 * @returns {ArrayMap<Contact>}
 */
function createState(data?: Contact[] | ArrayMap<Contact>): ArrayMap<Contact> {
	if (!data) return ArrayMapRegistry.clone<Contact>('contact', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<Contact>('contact', data.map(e => e));
    else return ArrayMapRegistry.clone<Contact>('contact', data);
}

function contacts(state: ArrayMap<Contact>, action: ContactAction): ArrayMap<Contact> {
	if (!state) state = createState();

	let st: ArrayMap<Contact>;

	switch (action.type) {
		// action.contact - the new value of contact with id action.id
		// action.contactId - id of the target, used if new value is null
		case Actions.CONTACT_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ contactReducer: FETCH_ONE_SUCCESS', action);

			if (action.contact === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.contactId)) {
				st = state.clone();
				st.set(action.contact);
				// state.clear();

				updateStorageNoReturn([action.contact]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.contacts
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.contacts
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.contacts
			 !partial, !stateEmpty, fetchEmpty	-> D return contacts in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge contacts in state newer than those in action.contacts
		 */
		case Actions.CONTACT_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.contacts.length === 0;
			const fetched = action.contacts.map(contact => new Contact({...contact, persisted: true}));

			if (logging) console.debug(`⮂ contactReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} contacts with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ contactReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ contactReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<Contact> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: Contact[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(contact => {
					const current = st.get(contact.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && contact.status === 'D') {
							// Remove it from state
							toUpdate.push(contact);
							st.delete(contact);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (contact.addedOn > current.addedOn) {
								toUpdate.push(contact);
								st.set(contact);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (contact.status !== 'D') {
							toUpdate.push(contact);
							st.set(contact);
						}
						else if (logging) console.debug('⮂ contactReducer: partialMerge: skipping deleted contact not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ contactReducer: empty: contacts', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ contactReducer: fullMerge: contacts', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localContact => {
                    // If this contact has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localContact.persisted) {
                        const fetchedContact = st.get(localContact.id);
                        if (!fetchedContact || localContact.addedOn > fetchedContact.addedOn) st.set(localContact);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.CONTACT_FETCH_ONE_FAILURE:
			console.error('CONTACT_FETCH_ONE_FAILURE', action.contactId, action.err);

			// No update to storage required
			return state;

		case Actions.CONTACT_FETCH_FAILURE:
			console.error('CONTACT_FETCH_FAILURE', action.contactId, action.err);

			// No update to storage required
			return state;

		case Actions.CONTACT_RESTORE: {
		    let contact = new Contact({...action.contact});
            st = state.clone();

            const existing = st.get(contact.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (contact.addedOn >= existing.addedOn) {
                    st.set(contact);
                    return updateStorage(st, [contact]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (contact.status === 'D') return state;

                st.set(contact);
                return updateStorage(st, [contact]);
            }
        }
            
		case Actions.CONTACT_SAVE: {
		    // console.debug('⮂ CONTACT_SAVE reducer', action.contact?.id);
		    
			// clone the contact
			let contact = new Contact({...action.contact});

			st = state.clone();
			// state.clear(); 
			
			
			// Clear computed fields
			contact.lf = null;
			contact.lfm = null;
			contact.fl = null;
			contact.fml = null;
			contact.uniqueLFM = null;
			contact.uniqueLFMComputed = null;
			contact.uniqueFML = null;
			contact.uniqueFMLComputed = null;

			// Add or update the new contact in the state
			// If this is a create, add to the queue
			const _contact = st.get(contact.id);

			if (!_contact) st.set(contact);
			else {
				if (contact.addedOn >= _contact.addedOn) st.set(contact);
			}

			return updateStorage(st, [contact]);
		}

		case Actions.CONTACT_SAVE_MANY: {
		    if (logging) console.debug('⮂ CONTACT_SAVE_MANY reducer', action.contacts.map(contact => contact.id).join(','));
		    
			// clone the contacts
			let toProcess = action.contacts.map(contact => new Contact(contact));

			st = state.clone();
			// state.clear();

			const updates: Contact[] = [];

			toProcess.forEach(contact => {
				// Add or update the new contact in the state
				// If this is a create, add to the queue
				const existing = st.get(contact.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (contact.status !== 'D') {
						st.push(contact);
						updates.push(contact);
					}
					else if (logging) console.debug('⮂ CONTACT_SAVE_MANY: skipping deleted contact not in state');
				}
				else {
					if (contact.addedOn >= existing.addedOn) {
						st.set(contact);
						updates.push(contact);
					}
					else if (logging) console.debug('⮂ Skipping, existing contact had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.CONTACT_REMOVE: {
			// clone the contact
			const contact = new Contact({...action.contact});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(contact);

			return updateStorage(st, [contact]);
		}

		case Actions.CONTACT_RESTORE_FAILURE:
		case Actions.CONTACT_SAVE_FAILURE: {
		    console.warn('CONTACT_SAVE_FAILURE reducer', action.contact?.id);
		    if (!action.contact?.id) {
				console.warn('CONTACT_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const contact = ensure(Contact, action.contact);
			if (!contact) {
				console.error('Could not find contact after call to save');
				return state;
			}

			st = state.clone();

			const _contactRef = state.get(contact.id);

			if (_contactRef && (_contactRef.addedOn === contact.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _contact = new Contact({..._contactRef});
				_contact.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_contact.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('contact', _contact).then(r => {
						if (r.isErr()) console.error(`⮂ contactReducer: failed to save contact with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ contactReducer: saved contact with fatalError info');
					}).catch(err => console.error(`⮂ contactReducer: failed to save contact with fatalError info: ${err.message}`));

				st.set(_contact);
			}

			return updateStorage(st);
		}

		case Actions.CONTACT_SAVE_MANY_FAILURE: {
		    console.warn('CONTACT_SAVE_MANY_FAILURE reducer', action.contacts.map(contact => contact.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.CONTACT_RESTORE_SUCCESS:
		case Actions.CONTACT_SAVE_SUCCESS: {
			if (!action.contact?.id) {
				console.warn('CONTACT_SAVE_SUCCESS reducer');
                return state;
			}

			const contactRef = state.get(action.contact?.id);
			if (!contactRef) {
				console.warn('Could not find contact after call to save');
                return state;
			}

			st = state.clone();

			if (action.contact.addedOn >= contactRef.addedOn) {
				st.set(action.contact);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const contact = new Contact({...contactRef, persisted: true});
				st.set(contact);
			}

			return updateStorage(st, [action.contact]);
		}

		case Actions.CONTACT_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ CONTACT_SAVE_MANY_SUCCESS reducer', action.contacts.map(contact => contact.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const contacts: Contact[] = [];

			action.contacts.forEach(e => {
				const contactRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!contactRef) console.warn('Could not find contact after call to save');
				else if (e.addedOn >= contactRef.addedOn) {
				    st.set(e);
				    contacts.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const contact = new Contact({...contactRef, persisted: true});
					st.set(contact);
					contacts.push(contact);
				}
			});

			return updateStorage(st, contacts);
		}

		case Actions.CONTACT_REMOVE_FAILURE: {
		    console.warn('CONTACT_REMOVE_FAILURE action', action.contact?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.CONTACT_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.CONTACT_RESTORE_MANY: {
			if (logging) console.debug('⮂ CONTACT_RESTORE_MANY reducer', action.contacts.map(contact => contact.id).join(','));

			const toProcess = action.contacts.map(contact => new Contact({...contact}));
			st = state.clone();

			const updates: Contact[] = [];

			toProcess.forEach(contact => {
				const existing = st.get(contact.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (contact.addedOn >= existing.addedOn) {
						st.set(contact);
						updates.push(contact);
					}
					else if (logging) console.debug('⮂ CONTACT_RESTORE_MANY: skipping older version of contact');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (contact.status !== 'D') {
						st.set(contact);
						updates.push(contact);
					}
					else if (logging) console.debug('⮂ CONTACT_RESTORE_MANY: skipping deleted contact not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.CONTACT_REMOVE_MANY: {
			if (logging) console.debug('⮂ CONTACT_REMOVE_MANY reducer', action.contacts.map(contact => contact.id).join(','));

			const toProcess = action.contacts.map(contact => new Contact({...contact}));
			st = state.clone();

			toProcess.forEach(contact => {
				st.delete(contact);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default contacts;